import AnimationCursorChoiceButton from 'js/editor/AnimationPanels/AnimationCursorChoiceButton';
import CursorChoiceButton from 'js/editor/AnimationPanels/CursorChoiceButton';
import DirectionChoiceButton from 'js/editor/AnimationPanels/DirectionChoiceButton';
import DirectionMenu from 'js/editor/AnimationPanels/DirectionMenu';
import DrawTypeChoiceButton from 'js/editor/AnimationPanels/DrawTypeChoiceButton';
import DrawTypeMenu from 'js/editor/AnimationPanels/DrawTypeMenu';
import PenMenu from 'js/editor/AnimationPanels/PenMenu';
import SpinChoiceButton from 'js/editor/AnimationPanels/SpinChoiceButton';
import SpinDirectionMenu from 'js/editor/AnimationPanels/SpinDirectionMenu';
import DragHandMenu from 'js/editor/DragHandMenu';
import DrawHandMenu from 'js/editor/DrawHandMenu';
import { isSVGElement } from 'js/editor/EditorCanvas/helpers/isSVGElement';
import EraseHandPicker from 'js/editor/EraseHandPicker';
import { ReactComponent as IconAnimationCounterClockwise } from 'imgs/svg-icons/IconAnimationCounterClockwise.svg';
import { ReactComponent as IconAnimationClockwise } from 'imgs/svg-icons/IconAnimationClockwise.svg';
import { ReactComponent as IconArrowEast } from 'imgs/svg-icons/IconArrowEast.svg';
import { ReactComponent as IconArrowNorth } from 'imgs/svg-icons/IconArrowNorth.svg';
import { ReactComponent as IconArrowNorthEast } from 'imgs/svg-icons/IconArrowNorthEast.svg';
import { ReactComponent as IconArrowNorthWest } from 'imgs/svg-icons/IconArrowNorthWest.svg';
import { ReactComponent as IconArrowSouth } from 'imgs/svg-icons/IconArrowSouth.svg';
import { ReactComponent as IconArrowSouthEast } from 'imgs/svg-icons/IconArrowSouthEast.svg';
import { ReactComponent as IconArrowSouthWest } from 'imgs/svg-icons/IconArrowSouthWest.svg';
import { ReactComponent as IconArrowWest } from 'imgs/svg-icons/IconArrowWest.svg';
import { ReactComponent as IconAnimationBounceIn } from 'imgs/svg-icons/IconAnimationBounceIn.svg';
import { ReactComponent as IconAnimationDisappear } from 'imgs/svg-icons/IconAnimationDisappear.svg';
import { ReactComponent as IconAnimationDragIn } from 'imgs/svg-icons/IconAnimationDragIn.svg';
import { ReactComponent as IconAnimationFade } from 'imgs/svg-icons/IconAnimationFade.svg';
import { ReactComponent as IconAnimationMoveIn } from 'imgs/svg-icons/IconAnimationMoveIn.svg';
import { ReactComponent as IconAnimationMoveOut } from 'imgs/svg-icons/IconAnimationMoveOut.svg';
import { ReactComponent as IconAnimationPulse } from 'imgs/svg-icons/IconAnimationPulse.svg';
import { ReactComponent as IconAnimationShake } from 'imgs/svg-icons/IconAnimationShake.svg';
import { ReactComponent as IconAnimationSlingshot } from 'imgs/svg-icons/IconAnimationSlingshot.svg';
import { ReactComponent as IconAnimationSpinLoop } from 'imgs/svg-icons/IconAnimationSpinLoop.svg';
import { ReactComponent as IconDraw } from 'imgs/svg-icons/IconDraw.svg';
import { ReactComponent as IconHandDraw } from 'imgs/svg-icons/IconHandDraw.svg';
import { ReactComponent as IconNoAnimation } from 'imgs/svg-icons/IconNoAnimation.svg';
import { ReactComponent as IconPenDraw } from 'imgs/svg-icons/IconPenDraw.svg';
import {
  AllAnimationTypeKeys,
  AnimationConfigurationOptions,
  AnimationTypeOption,
  DrawType,
  ElementChoiceButtonProps,
  ElementMenuProps,
  EmphasisAnimationTypeKeys,
  EntranceAnimationTypeKeys,
  ExitAnimationTypeKeys,
  VSImageShapeOrTextElementModel
} from 'js/types';

import { ReactComponent as IconErase } from '../../imgs/svg-icons/IconErase.svg';
import { ReactComponent as IconIllustrate } from '../../imgs/svg-icons/IconIllustrate.svg';
import { ReactComponent as IconReveal } from '../../imgs/svg-icons/IconReveal.svg';

import {
  BOUNCE_IN_KEY,
  BOUNCE_LOOP_KEY,
  DEFAULT_DRAG_CURSOR_ID,
  DEFAULT_ERASE_CURSOR_ID,
  DEFAULT_HAND_CURSOR_ID,
  DEFAULT_PEN_CURSOR_ID,
  DISAPPEAR_KEY,
  DRAG_IN_KEY,
  DRAW_KEY,
  EXIT_ANIMATION_ERASE_KEY,
  FADE_IN_KEY,
  FADE_OUT_KEY,
  HAND_DRAW_KEY,
  MOVE_IN_KEY,
  MOVE_OUT_KEY,
  NO_ANIMATION_KEY,
  PEN_DRAW_KEY,
  PULSE_LOOP_KEY,
  SHAKE_LOOP_KEY,
  SLINGSHOT_KEY,
  SPIN_LOOP_KEY
} from './consts';
import { SPIN_CLOCKWISE_DEFAULT, SPIN_COUNTER_CLOCKWISE_DEFAULT } from './defaults';

export const DRAW_TYPE_MENU = {
  title: 'Draw style',
  ChoiceButton: DrawTypeChoiceButton,
  choiceButtonLabel: 'Draw style',
  Component: DrawTypeMenu
};

export const getEntranceAnimationOptions = (
  activeElement?: VSImageShapeOrTextElementModel,
  activeElements: Array<VSImageShapeOrTextElementModel> = []
) => {
  const allElementsAreSVG = !!activeElements.length && activeElements.every(element => isSVGElement(element));
  const allElementsSameAnimationType =
    !!activeElements.length &&
    activeElements.every(
      element =>
        (element.entranceAnimation?.id ?? HAND_DRAW_KEY) === (activeElements[0].entranceAnimation?.id ?? HAND_DRAW_KEY)
    );

  const animationTypeHasDrawStyle = (option: AllAnimationTypeKeys) =>
    [HAND_DRAW_KEY, PEN_DRAW_KEY, DRAW_KEY].includes(option);

  const allElementsHaveDrawStyle =
    !!activeElements.length &&
    activeElements.every(
      element => !element.entranceTween && animationTypeHasDrawStyle(element.entranceAnimation?.id ?? HAND_DRAW_KEY)
    );

  const allElementsSameSecondaryDrawType =
    !!activeElements.length &&
    activeElements.every(
      element =>
        !!element.entranceAnimation &&
        element.entranceAnimation.config?.drawType === activeElements[0].entranceAnimation?.config?.drawType
    );

  const options = ENTRANCE_ANIMATION_OPTIONS.map(option => {
    const showDrawTypeAsTertiaryOption =
      (isSVGElement(activeElement) && [HAND_DRAW_KEY, PEN_DRAW_KEY].includes(option.type)) ||
      (allElementsAreSVG && allElementsSameAnimationType && [HAND_DRAW_KEY, PEN_DRAW_KEY].includes(option.type)) ||
      (allElementsAreSVG &&
        allElementsSameAnimationType &&
        option.type === DRAW_KEY &&
        !allElementsSameSecondaryDrawType);
    const isCurrentOptionHasDrawStyle = animationTypeHasDrawStyle(option.type as EntranceAnimationTypeKeys);
    const showDrawTypeAsSecondaryOption =
      (isSVGElement(activeElement) && option.type === DRAW_KEY) ||
      (allElementsAreSVG && allElementsSameAnimationType && option.type === DRAW_KEY) ||
      (allElementsAreSVG && !allElementsSameAnimationType && isCurrentOptionHasDrawStyle && allElementsHaveDrawStyle);

    if (showDrawTypeAsTertiaryOption) {
      return {
        ...option,
        tertiaryMenu: DRAW_TYPE_MENU
      };
    }

    if (showDrawTypeAsSecondaryOption) {
      return {
        ...option,
        secondaryMenu: DRAW_TYPE_MENU
      };
    }

    return option;
  });

  return options;
};

export const ENTRANCE_ANIMATION_OPTIONS: Array<AnimationTypeOption<ElementChoiceButtonProps, ElementMenuProps>> = [
  {
    type: NO_ANIMATION_KEY,
    title: 'None',
    Icon: IconNoAnimation
  },
  {
    type: HAND_DRAW_KEY,
    title: 'Hand draw',
    Icon: IconHandDraw,

    secondaryMenu: {
      title: 'Hand type',
      Component: DrawHandMenu,
      choiceButtonLabel: 'Hand',
      ChoiceButton: CursorChoiceButton
    }
  },
  {
    type: PEN_DRAW_KEY,
    title: 'Pen draw',
    Icon: IconPenDraw,

    secondaryMenu: {
      title: 'Pen type',
      Component: PenMenu,
      choiceButtonLabel: 'Pen',
      ChoiceButton: CursorChoiceButton
    }
  },
  {
    type: DRAW_KEY,
    title: 'Draw',
    Icon: IconDraw
  },
  {
    type: FADE_IN_KEY,
    title: 'Fade in',
    Icon: IconAnimationFade
  },
  {
    type: MOVE_IN_KEY,
    title: 'Move in',
    Icon: IconAnimationMoveIn,

    secondaryMenu: {
      title: 'Direction',
      Component: DirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: DirectionChoiceButton
    }
  },
  {
    type: DRAG_IN_KEY,
    title: 'Drag in',
    Icon: IconAnimationDragIn,
    secondaryMenu: {
      title: 'Drag Hand',
      Component: DragHandMenu,
      choiceButtonLabel: 'Hand',
      ChoiceButton: CursorChoiceButton
    },

    tertiaryMenu: {
      title: 'Direction',
      Component: DirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: DirectionChoiceButton
    }
  },
  {
    type: BOUNCE_IN_KEY,
    title: 'Bounce in',
    Icon: IconAnimationBounceIn,

    secondaryMenu: {
      title: 'Direction',
      Component: DirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: DirectionChoiceButton
    }
  }
];

export const EMPHASIS_ANIMATION_OPTIONS: Array<AnimationTypeOption<ElementChoiceButtonProps, ElementMenuProps>> = [
  {
    type: NO_ANIMATION_KEY,
    title: 'None',
    Icon: IconNoAnimation
  },
  {
    type: PULSE_LOOP_KEY,
    title: 'Pulse',
    Icon: IconAnimationPulse
  },
  {
    type: SPIN_LOOP_KEY,
    title: 'Spin',
    Icon: IconAnimationSpinLoop,
    secondaryMenu: {
      title: 'Direction',
      Component: SpinDirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: SpinChoiceButton
    }
  },
  {
    type: SHAKE_LOOP_KEY,
    title: 'Shake',
    Icon: IconAnimationShake,

    secondaryMenu: {
      title: 'Direction',
      Component: DirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: DirectionChoiceButton
    }
  },
  {
    type: BOUNCE_LOOP_KEY,
    title: 'Bounce',
    Icon: IconAnimationBounceIn
  }
];

export const EXIT_ANIMATION_OPTIONS: Array<AnimationTypeOption<ElementChoiceButtonProps, ElementMenuProps>> = [
  {
    type: NO_ANIMATION_KEY,
    title: 'None',
    Icon: IconNoAnimation
  },
  {
    type: FADE_OUT_KEY,
    title: 'Fade out',
    Icon: IconAnimationFade
  },
  {
    type: MOVE_OUT_KEY,
    title: 'Move out',
    Icon: IconAnimationMoveOut,

    secondaryMenu: {
      title: 'Direction',
      Component: DirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: DirectionChoiceButton
    }
  },
  {
    type: SLINGSHOT_KEY,
    title: 'Slingshot',
    Icon: IconAnimationSlingshot,

    secondaryMenu: {
      title: 'Direction',
      Component: DirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: DirectionChoiceButton
    }
  },
  {
    type: DISAPPEAR_KEY,
    title: 'Disappear',
    Icon: IconAnimationDisappear
  },
  {
    type: EXIT_ANIMATION_ERASE_KEY,
    title: 'Erase',
    Icon: IconErase,
    secondaryMenu: {
      title: 'Hand Type',
      Component: EraseHandPicker,
      choiceButtonLabel: 'Hand',
      ChoiceButton: AnimationCursorChoiceButton
    }
  }
];

export const ENTRANCE_ANIMATION_DURATION_DOCUMENT_KEY = 'animationTime';
export const EMPHASIS_ANIMATION_DURATION_DOCUMENT_KEY = 'emphasisAnimationTime';
export const EMPHASIS_ANIMATION_LOOPS_DOCUMENT_KEY = 'emphasisAnimationLoops';
export const EXIT_ANIMATION_DURATION_DOCUMENT_KEY = 'exitAnimationTime';
export const PAUSE_DURATION_DOCUMENT_KEY = 'pauseTime';

export interface SelectableAnimationPositionOption {
  id: number;
  label: string;
  Icon?: React.ComponentType;
  value?: string;
}
export interface SelectableAnimationDirectionOption {
  id: number;
  label: string;
  Icon?: React.ComponentType;
  direction?: string;
}

export const entranceAnimationDirectionOptions: SelectableAnimationPositionOption[] = [
  {
    id: 0,
    label: 'Top left',
    Icon: IconArrowSouthEast,
    value: '@tl'
  },
  {
    id: 1,
    label: 'Top',
    Icon: IconArrowSouth,
    value: '@t'
  },
  {
    id: 2,
    label: 'Top right',
    Icon: IconArrowSouthWest,
    value: '@tr'
  },
  {
    id: 3,
    label: 'Left',
    Icon: IconArrowEast,
    value: '@l'
  },
  {
    id: 4,
    label: 'Scene Thumbnail'
  },
  {
    id: 5,
    label: 'Right',
    Icon: IconArrowWest,
    value: '@r'
  },
  {
    id: 6,
    label: 'Bottom left',
    Icon: IconArrowNorthEast,
    value: '@bl'
  },
  {
    id: 7,
    label: 'Bottom',
    Icon: IconArrowNorth,
    value: '@b'
  },
  {
    id: 8,
    label: 'Bottom right',
    Icon: IconArrowNorthWest,
    value: '@br'
  }
];

export const exitAnimationDirectionOptions: SelectableAnimationPositionOption[] = [
  {
    id: 0,
    label: 'Top left',
    Icon: IconArrowNorthWest,
    value: '@tl'
  },
  {
    id: 1,
    label: 'Top',
    Icon: IconArrowNorth,
    value: '@t'
  },
  {
    id: 2,
    label: 'Top right',
    Icon: IconArrowNorthEast,
    value: '@tr'
  },
  {
    id: 3,
    label: 'Left',
    Icon: IconArrowWest,
    value: '@l'
  },
  {
    id: 4,
    label: 'Scene Thumbnail'
  },
  {
    id: 5,
    label: 'Right',
    Icon: IconArrowEast,
    value: '@r'
  },
  {
    id: 6,
    label: 'Bottom left',
    Icon: IconArrowSouthWest,
    value: '@bl'
  },
  {
    id: 7,
    label: 'Bottom',
    Icon: IconArrowSouth,
    value: '@b'
  },
  {
    id: 8,
    label: 'Bottom right',
    Icon: IconArrowSouthEast,
    value: '@br'
  }
];

export const selectableSpinDirectionOptions = [
  {
    id: 0,
    label: 'Clockwise',
    Icon: IconAnimationClockwise,
    value: SPIN_CLOCKWISE_DEFAULT
  },
  {
    id: 1,
    label: 'Counter Clockwise',
    Icon: IconAnimationCounterClockwise,
    value: SPIN_COUNTER_CLOCKWISE_DEFAULT
  }
];

export const DRAW_TYPE_REVEAL = 'reveal';
export const DRAW_TYPE_ILLUSTRATE = 'illustrate';
export const selectableDrawTypeOptions = [
  {
    id: DRAW_TYPE_ILLUSTRATE,
    label: 'Illustrate',
    Icon: IconIllustrate,
    value: DRAW_TYPE_ILLUSTRATE as DrawType
  },
  {
    id: DRAW_TYPE_REVEAL,
    label: 'Reveal',
    Icon: IconReveal,
    value: DRAW_TYPE_REVEAL as DrawType
  }
];

export const selectableShakeAnimationOptions: SelectableAnimationDirectionOption[] = [
  {
    id: 0,
    label: 'Top left',
    Icon: IconArrowNorthWest,
    direction: 'tl'
  },
  {
    id: 1,
    label: 'Top',
    Icon: IconArrowNorth,
    direction: 't'
  },
  {
    id: 2,
    label: 'Top right',
    Icon: IconArrowNorthEast,
    direction: 'tr'
  },
  {
    id: 3,
    label: 'Left',
    Icon: IconArrowWest,
    direction: 'l'
  },
  {
    id: 4,
    label: 'Scene Thumbnail'
  },
  {
    id: 5,
    label: 'Right',
    Icon: IconArrowEast,
    direction: 'r'
  },
  {
    id: 6,
    label: 'Bottom left',
    Icon: IconArrowSouthWest,
    direction: 'bl'
  },
  {
    id: 7,
    label: 'Bottom',
    Icon: IconArrowSouth,
    direction: 'b'
  },
  {
    id: 8,
    label: 'Bottom right',
    Icon: IconArrowSouthEast,
    direction: 'br'
  }
];

export const EXIT_ANIMATION_CURSOR_DEFAULTS: Record<ExitAnimationTypeKeys, AnimationConfigurationOptions> = {
  [EXIT_ANIMATION_ERASE_KEY]: { cursorId: DEFAULT_ERASE_CURSOR_ID },
  [FADE_OUT_KEY]: { cursorId: undefined },
  [MOVE_OUT_KEY]: { cursorId: undefined },
  [SLINGSHOT_KEY]: { cursorId: undefined },
  [DISAPPEAR_KEY]: { cursorId: undefined },
  [NO_ANIMATION_KEY]: { cursorId: undefined }
};

export const ENTRANCE_ANIMATION_CURSOR_DEFAULTS: Record<EntranceAnimationTypeKeys, AnimationConfigurationOptions> = {
  [DRAW_KEY]: { cursorId: undefined },
  [PEN_DRAW_KEY]: { cursorId: DEFAULT_PEN_CURSOR_ID },
  [HAND_DRAW_KEY]: { cursorId: DEFAULT_HAND_CURSOR_ID },
  [FADE_IN_KEY]: { cursorId: undefined },
  [MOVE_IN_KEY]: { cursorId: undefined },
  [BOUNCE_IN_KEY]: { cursorId: undefined },
  [DRAG_IN_KEY]: { cursorId: DEFAULT_DRAG_CURSOR_ID },
  [NO_ANIMATION_KEY]: { cursorId: undefined }
};

export const EMPHASIS_ANIMATION_CURSOR_DEFAULTS: Record<EmphasisAnimationTypeKeys, AnimationConfigurationOptions> = {
  [BOUNCE_LOOP_KEY]: { cursorId: undefined },
  [SPIN_LOOP_KEY]: { cursorId: undefined },
  [PULSE_LOOP_KEY]: { cursorId: undefined },
  [SHAKE_LOOP_KEY]: { cursorId: undefined },
  [NO_ANIMATION_KEY]: { cursorId: undefined }
};

export const ALL_ANIMATION_CURSOR_DEFAULTS: Record<AllAnimationTypeKeys, AnimationConfigurationOptions> = {
  ...EMPHASIS_ANIMATION_CURSOR_DEFAULTS,
  ...EXIT_ANIMATION_CURSOR_DEFAULTS,
  ...ENTRANCE_ANIMATION_CURSOR_DEFAULTS
};

export const EMPHASIS_LOOPS_TIMING_DEFAULTS = {
  [BOUNCE_LOOP_KEY]: {
    loops: 3,
    duration: 1
  },
  [SPIN_LOOP_KEY]: {
    loops: 1,
    duration: 1
  },
  [PULSE_LOOP_KEY]: {
    loops: 1,
    duration: 1
  },
  [SHAKE_LOOP_KEY]: {
    loops: 3,
    duration: 1
  },
  [NO_ANIMATION_KEY]: {
    loops: 0,
    duration: 0
  }
};

export const EXIT_TIMING_DEFAULTS = {
  [SLINGSHOT_KEY]: 1,
  [MOVE_OUT_KEY]: 1,
  [EXIT_ANIMATION_ERASE_KEY]: 1,
  [FADE_OUT_KEY]: 1,
  [DISAPPEAR_KEY]: 0,
  [NO_ANIMATION_KEY]: 0
};
